// Login/setup screen and the security controls used by Settings.

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";
import { Logo } from "./components/Logo";

export interface AuthState {
  authenticated: boolean;
  setupRequired?: boolean;
  disabled?: boolean;
  account?: {
    id: number;
    username: string;
    role: "owner" | "member";
    totpEnabled: boolean;
    /** Response style; absent on servers that predate the pref. */
    mode?: "simple" | "technical";
    passkeys: { id: number; label: string; createdAt: string }[];
  };
}

export function useAuthState() {
  return useQuery<AuthState>({
    queryKey: ["auth"],
    queryFn: async () => {
      const res = await fetch("/api/auth/me");
      if (res.status === 404) {
        // Auth routes absent = AUTH_DISABLED dev mode.
        return { authenticated: true, disabled: true };
      }
      return (await res.json()) as AuthState;
    },
    staleTime: 60_000,
  });
}

async function postJson(url: string, body?: unknown) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as {
    error?: string;
    [k: string]: unknown;
  };
  if (!res.ok) throw new Error(data.error ?? `${res.status}`);
  return data;
}

export function LoginScreen({ setupRequired }: { setupRequired: boolean }) {
  const queryClient = useQueryClient();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [needTotp, setNeedTotp] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = () => queryClient.invalidateQueries();

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      if (setupRequired) {
        await postJson("/api/auth/setup", { username, password });
      } else {
        await postJson("/api/auth/login", {
          username,
          password,
          totp: totp || undefined,
        });
      }
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg === "totp-required") {
        setNeedTotp(true);
        setError("Enter your 6-digit authenticator code.");
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const passkeyLogin = async () => {
    setBusy(true);
    setError("");
    try {
      const options = await postJson("/api/auth/passkey/login-options");
      const response = await startAuthentication({
        optionsJSON: options as never,
      });
      await postJson("/api/auth/passkey/login-verify", { response });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--page)",
      }}
    >
      <div className="panel" style={{ width: "min(360px, calc(100vw - 32px))" }}>
        <h2
          style={{
            marginTop: 0,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <Logo size={30} />
          IdeaCrunch
        </h2>
        {setupRequired ? (
          <p style={{ color: "var(--ink-2)", fontSize: 13.5 }}>
            First boot: create the owner account. Add a passkey and 2FA from
            Settings right after.
          </p>
        ) : (
          <p style={{ color: "var(--ink-2)", fontSize: 13.5 }}>Sign in.</p>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <input
            style={inputStyle}
            placeholder="username"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <input
            style={inputStyle}
            type="password"
            autoComplete={setupRequired ? "new-password" : "current-password"}
            placeholder={setupRequired ? "password (12+ characters)" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void submit()}
          />
          {needTotp && (
            <input
              style={inputStyle}
              inputMode="numeric"
              placeholder="123456"
              value={totp}
              onChange={(e) => setTotp(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void submit()}
            />
          )}
          <button className="btn" disabled={busy} onClick={() => void submit()}>
            {setupRequired ? "Create account" : "Sign in"}
          </button>
          {!setupRequired && (
            <button
              className="btn secondary"
              disabled={busy}
              onClick={() => void passkeyLogin()}
            >
              Sign in with passkey
            </button>
          )}
          {error && (
            <div style={{ color: "var(--critical)", fontSize: 13.5 }}>
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  background: "var(--surface)",
  color: "var(--ink)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  padding: "8px 10px",
  font: "inherit",
};

export function SecuritySettings() {
  const { data } = useAuthState();
  const queryClient = useQueryClient();
  const [totpSetup, setTotpSetup] = useState<{
    secret: string;
    uri: string;
  } | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [msg, setMsg] = useState("");

  if (!data || data.disabled) {
    return (
      <div className="panel">
        <h2>Security</h2>
        <p className="parse-warnings">
          Auth is disabled (dev mode). The public deployment always runs with
          the login gate.
        </p>
      </div>
    );
  }
  if (!data.authenticated) return null;

  const enrollTotp = async () => {
    setMsg("");
    const setup = (await postJson("/api/auth/totp/setup")) as {
      secret: string;
      uri: string;
    };
    setTotpSetup(setup);
  };

  const enableTotp = async () => {
    try {
      await postJson("/api/auth/totp/enable", { code: totpCode });
      setTotpSetup(null);
      setMsg("2FA enabled.");
      await queryClient.invalidateQueries({ queryKey: ["auth"] });
    } catch (err) {
      setMsg(err instanceof Error ? err.message : String(err));
    }
  };

  const addPasskey = async () => {
    setMsg("");
    try {
      const options = await postJson("/api/auth/passkey/register-options");
      const response = await startRegistration({ optionsJSON: options as never });
      await postJson("/api/auth/passkey/register-verify", {
        response,
        label: `passkey ${new Date().toISOString().slice(0, 10)}`,
      });
      setMsg("Passkey registered.");
      await queryClient.invalidateQueries({ queryKey: ["auth"] });
    } catch (err) {
      setMsg(err instanceof Error ? err.message : String(err));
    }
  };

  const logout = async (all: boolean) => {
    await postJson(all ? "/api/auth/logout-all" : "/api/auth/logout");
    await queryClient.invalidateQueries();
  };

  return (
    <>
      <div className="panel">
        <h2>Security</h2>
        <p style={{ color: "var(--ink-2)", fontSize: 13.5 }}>
          Signed in as <strong>{data.account?.username}</strong>
          {data.account?.role === "owner" && (
            <span className="chip accent" style={{ marginLeft: 6 }}>
              owner
            </span>
          )}
          {" · "}2FA: {data.account?.totpEnabled ? "enabled" : "off"}
          {" · "}passkeys: {data.account?.passkeys.length ?? 0}
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="btn secondary" onClick={() => void addPasskey()}>
            Add a passkey
          </button>
          {!data.account?.totpEnabled && (
            <button className="btn secondary" onClick={() => void enrollTotp()}>
              Enable 2FA (TOTP)
            </button>
          )}
          <button className="btn secondary" onClick={() => void logout(false)}>
            Sign out
          </button>
          <button className="btn secondary" onClick={() => void logout(true)}>
            Sign out everywhere
          </button>
        </div>
        {totpSetup && (
          <div style={{ marginTop: 12 }}>
            <p style={{ fontSize: 13.5 }}>
              Add this secret to your authenticator app, then confirm a code:
            </p>
            <p className="artifact-caption" style={{ wordBreak: "break-all" }}>
              {totpSetup.secret}
              <br />
              {totpSetup.uri}
            </p>
            <input
              style={{ ...inputStyle, width: 140, marginRight: 8 }}
              inputMode="numeric"
              placeholder="123456"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value)}
            />
            <button className="btn" onClick={() => void enableTotp()}>
              Confirm
            </button>
          </div>
        )}
        {msg && <p style={{ color: "var(--ink-2)", fontSize: 13.5 }}>{msg}</p>}
      </div>
      {data.account?.role === "owner" && <UserManagement />}
    </>
  );
}

/**
 * Owner-only: add member accounts. Every member gets their OWN workspace -
 * their runs, library, and engine context are invisible to everyone else,
 * including the owner's runs to them.
 */
function UserManagement() {
  const queryClient = useQueryClient();
  const { data: users, refetch } = useQuery({
    queryKey: ["auth-users"],
    queryFn: async () =>
      (await (await fetch("/api/auth/users")).json()) as {
        id: number;
        username: string;
        role: string;
        totpEnabled: boolean;
        mode?: "simple" | "technical";
        passkeys: unknown[];
      }[],
  });
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState("");

  const setUserMode = async (id: number, mode: "simple" | "technical") => {
    setMsg("");
    try {
      await postJson(`/api/auth/users/${id}/mode`, { mode });
      await refetch();
      // The owner may have changed their own mode; refresh the session too.
      await queryClient.invalidateQueries({ queryKey: ["auth"] });
    } catch (err) {
      setMsg(err instanceof Error ? err.message : String(err));
    }
  };

  const generatePassword = () => {
    const raw = crypto.getRandomValues(new Uint8Array(12));
    setPassword(
      Array.from(raw, (b) => "abcdefghjkmnpqrstuvwxyz23456789"[b % 31]).join(""),
    );
  };

  const addUser = async () => {
    setMsg("");
    try {
      await postJson("/api/auth/users", { username, password });
      setMsg(
        `Created "${username}". Share the URL and this one-time password with them privately; they should add a passkey and change it on first sign-in.`,
      );
      setUsername("");
      await refetch();
    } catch (err) {
      setMsg(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="panel">
      <h2>Users</h2>
      <p style={{ color: "var(--ink-2)", fontSize: 13.5 }}>
        Each user gets a fully separate workspace: their ideas, runs, and
        library are invisible to every other user - and yours to them.
      </p>
      <ul style={{ paddingLeft: 18 }}>
        {(users ?? []).map((u) => (
          <li key={u.id} style={{ marginBottom: 4 }}>
            {u.username}{" "}
            <span className="chip ghost">{u.role}</span>{" "}
            {u.mode && (
              <button
                className="chip ghost clickable"
                title="Response style: simple = plain language with detail collapsed, technical = full framework vocabulary. Click to switch."
                onClick={() =>
                  void setUserMode(
                    u.id,
                    u.mode === "simple" ? "technical" : "simple",
                  )
                }
              >
                {u.mode === "simple" ? "simple mode" : "technical mode"}
              </button>
            )}
          </li>
        ))}
      </ul>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input
          style={{ ...inputStyle, width: 160 }}
          placeholder="new username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <input
          style={{ ...inputStyle, width: 200 }}
          placeholder="one-time password (12+)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <button className="btn secondary" onClick={generatePassword}>
          Generate
        </button>
        <button
          className="btn"
          disabled={username.trim().length < 2 || password.length < 12}
          onClick={() => void addUser()}
        >
          Add user
        </button>
      </div>
      {msg && <p style={{ color: "var(--ink-2)", fontSize: 13.5 }}>{msg}</p>}
    </div>
  );
}
